import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import { useNavigate, useLocation } from 'react-router-dom'
import { Menu, MenuList, MenuItem, IconButton, Drawer, Box } from '@mui/material'
import MenuOutlinedIcon from '@mui/icons-material/MenuOutlined'
import CloseIcon from '@mui/icons-material/Close'
import { withStyles } from '@mui/styles'

import { menuStyles } from './menu.styles'

/**
 * @typedef {object} MenuItem
 * @property {string} label - ID de traducción para la etiqueta del item del menú.
 * @property {string} [path] - Ruta de navegación del item.
 * @property {MenuItem[]} [children] - Sub-items del menú para crear menús desplegables.
 */

/**
 * @typedef {object} MenuComponentProps
 * @property {object} classes - Objeto con los nombres de las clases CSS inyectado por `withStyles`.
 * @property {MenuItem[]} items - Array de items del menú con sus rutas y posibles sub-items.
 * @property {boolean} isMobileWidth - Indica si el ancho de pantalla es móvil.
 * @property {boolean} mobileView - Fuerza la vista móvil independientemente del ancho de pantalla.
 */

/**
 * Componente de menú de navegación responsive con soporte para sub-menús y vista móvil.
 * 
 * Proporciona dos modos de visualización:
 * - **Desktop**: Menú horizontal con sub-menús desplegables
 * - **Móvil**: Drawer lateral con menú jerárquico
 * 
 * Características:
 * - **Navegación automática**: Integración con React Router
 * - **Items activos**: Resalta el item correspondiente a la ruta actual
 * - **Sub-menús**: Soporte para menús anidados con indicación visual
 * - **Internacionalización**: Etiquetas traducidas mediante react-intl
 * - **Responsive**: Adaptación automática según el tamaño de pantalla
 * 
 * @component
 * @example
 * const menuItems = [
 *   { label: 'menu.home', path: '/home' },
 *   {
 *     label: 'menu.settings',
 *     children: [
 *       { label: 'menu.profile', path: '/settings/profile' },
 *       { label: 'menu.security', path: '/settings/security' },
 *     ],
 *   },
 * ];
 * 
 * <MenuComponent items={menuItems} isMobileWidth={false} mobileView={false} />
 * 
 * @param {MenuComponentProps} props - Las props del componente MenuComponent.
 */
export const MenuComponent = withStyles(menuStyles)(({
  classes,
  items,
  isMobileWidth,
  mobileView,
}) => {
  const { formatMessage } = useIntl()
  const navigate = useNavigate()
  const location = useLocation()

  const [drawerOpen, setDrawerOpen] = useState(false)

  const handleNavigate = (path) => {
    setDrawerOpen(!drawerOpen)
    navigate(path)
  }

  const toggleDrawer = (event) => {
    if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
      return
    }
    setDrawerOpen(!drawerOpen)
  }

  const MenuItemCollapsable = ({ item }) => {
    const [anchorEl, setAnchorEl] = React.useState(null)

    const handleClick = (event) => {
      setAnchorEl(event.currentTarget)
    }

    const handleClose = () => {
      setAnchorEl(null)
    }

    return (
      <>
        <MenuItem
          classeName={classes.menuItem}
          selected={item.children.some((item) => item.path === location.pathname)}
          onClick={handleClick}
        >
          {formatMessage({ id: item.label })}
        </MenuItem>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleClose}
          getContentAnchorEl={null}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
        >
          <MenuItems items={item.children} />
        </Menu>
      </>
    )
  }

  const MenuItems = ({ items }) =>
    items.map((item, i) => (
      <MenuItem
        key={i}
        selected={item.path === location.pathname}
        onClick={() => handleNavigate(item.path)}
        className={classes.menuItem}
      >
        {formatMessage({ id: item.label })}
      </MenuItem>
    ))

  return isMobileWidth || mobileView ? (
    <>
      <IconButton onClick={toggleDrawer}>
        <MenuOutlinedIcon className={classes.menuIcon} />
      </IconButton>
      <Drawer anchor="left" open={drawerOpen} onClose={toggleDrawer}>
        <Box p={2}>
          <Box mb={1}>
            <IconButton onClick={toggleDrawer}>
              <CloseIcon className={classes.closeIcon} />
            </IconButton>
          </Box>
          {items.map((item, i) => {
            return (
              <Box key={i}>
                {item.children && item.children.length ? (
                  <Box key={i}>
                    <Box key={i} pb={1} fontSize={20}>
                      <MenuItem
                        classes={{ root: classes.menuItem, selected: classes.selectedMenuItem }}
                        selected={item.children.some((item) => item.path === location.pathname)}
                      >
                        {formatMessage({ id: item.label })}
                      </MenuItem>
                    </Box>
                    <Box ml={4}>
                      <MenuItems items={item.children} />
                    </Box>
                  </Box>
                ) : (
                  <MenuItem
                    key={i}
                    selected={item.path === location.pathname}
                    onClick={() => handleNavigate(item.path)}
                    classes={{ root: classes.menuItem, selected: classes.selectedMenuItem }}
                  >
                    {formatMessage({ id: item.label })}
                  </MenuItem>
                )}
              </Box>
            )
          })}
        </Box>
      </Drawer>
    </>
  ) : (
    <MenuList className={classes.horizontal}>
      {items.map((item, i) => {
        return (
          <Box key={i}>
            {item.children && item.children.length ? (
              <MenuItemCollapsable item={item} />
            ) : (
              <MenuItem
                key={i}
                selected={item.path === location.pathname}
                onClick={() => handleNavigate(item.path)}
                classes={{ root: classes.menuItem, selected: classes.selectedMenuItem }}
              >
                {formatMessage({ id: item.label })}
              </MenuItem>
            )}
          </Box>
        )
      })}
    </MenuList>
  )
})
